import { PhoneOutline24Icon } from "@vygruppen/spor-icon-react";
import { useGetTrainStations } from "features/VehicleSidebar/VehicleList/AddVehicles/useInfoDataForVehicles";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { CallButton } from "shared/components/buttons/CallButton";
import { TimeInterval } from "shared/components/dataDisplay/TimeInterval";
import {
  Badge,
  Box,
  Button,
  Flex,
  Text,
  tokens,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "shared/components/Spor";
import type { StopReference } from "shared/types/alternativeTransport";
import { VehicleType } from "shared/types/alternativeTransport";
import { toSentenceCase } from "shared/utils/alternativeTransport";
import styled from "styled-components";

const SummaryLayout = styled(Flex)<{ $responsiveThreshold: number }>`
  gap: ${tokens.default.size.spacing[2]};
  @container altTransportPage (width < ${({ $responsiveThreshold }) =>
    $responsiveThreshold}px) {
    flex-direction: column;
    gap: ${tokens.default.size.spacing[0.5]};
  }
`;

const SummaryField = styled(Flex)<{ $responsiveThreshold: number }>`
  @container altTransportPage (width < ${({ $responsiveThreshold }) =>
    $responsiveThreshold}px) {
    && {
      flex-direction: row;
      min-width: 0;
      align-items: center;
      gap: ${tokens.default.size.spacing[1.5]};
    }
  }
`;

const SummaryFieldTitle = styled(Text)<{ $responsiveThreshold: number }>`
  @container altTransportPage (width < ${({ $responsiveThreshold }) =>
    $responsiveThreshold}px) {
    min-width: 120px;

    flex-shrink: 0;
  }
`;

// Finds the train station corresponding to the given base stop reference.
const useBaseStopStation = (baseStopReference?: StopReference | null) => {
  const { data } = useGetTrainStations(
    Boolean(baseStopReference?.jbvId || baseStopReference?.nsrCode),
  );

  return useMemo(
    () =>
      data?.railStations.find((trainStation) =>
        trainStation.externalIds.some(
          (externalId) =>
            (externalId.sourceSystem === "TRAINPLAN" &&
              externalId.id === baseStopReference?.jbvId) ||
            (externalId.sourceSystem === "NSR" &&
              externalId.id === baseStopReference?.nsrCode),
        ),
      ),
    [data?.railStations, baseStopReference?.jbvId, baseStopReference?.nsrCode],
  );
};

const getVehicleText = (vehicleType: VehicleType) => {
  const { t } = useTranslation();
  switch (vehicleType) {
    case "BUS":
      return t("Buss");
    case "MINIBUS":
      return t("Minibuss");
    case "TAXI":
      return t("Taxi");
    case "MAXITAXI":
      return t("MaxiTaxi");
    case "UNKNOWN":
      return t("Ukjent kjøretøy");
  }
};

const Component = ({
  title,
  size = "md",
  child,
  responsiveThreshold,
}: {
  title: string;
  size?: "sm" | "md" | "lg";
  responsiveThreshold: number;

  child: string | React.ReactNode;
}) => {
  let width = "90px";

  if (size === "md") {
    width = "120px";
  } else if (size === "lg") {
    width = "240px";
  }

  return (
    <SummaryField
      direction="column"
      gap="0.5"
      minWidth={width}
      $responsiveThreshold={responsiveThreshold}
    >
      <SummaryFieldTitle
        fontWeight="700"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        overflow="hidden"
        $responsiveThreshold={responsiveThreshold}
      >
        {title}
      </SummaryFieldTitle>
      {typeof child === "string" ? (
        <Tooltip>
          <TooltipTrigger width={width}>
            <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
              {child}
            </Text>
          </TooltipTrigger>
          <TooltipContent showArrow>{child}</TooltipContent>
        </Tooltip>
      ) : (
        child
      )}
    </SummaryField>
  );
};

type VehicleSummaryCommonProps = {
  type: VehicleType;
  driverPhoneNumber: string | null;
  operatorName: string | null;
};

type VehicleSummaryProps = VehicleSummaryCommonProps &
  (
    | {
        variant: "operational";
        stations?: string | null;
        trains?: string | null;
        lineNames?: string | null;
        startTime?: Date | null;
        endTime?: Date | null;
        reference?: string | null;
        availableFrom?: never;
        availableTo?: never;
        location?: never;
        baseStopReference?: never;
      }
    | {
        variant: "reserve-backup";
        availableFrom?: Date | null;
        availableTo?: Date | null;
        location?: string | null;
        reference?: string | null;
        baseStopReference?: StopReference | null;
        stations?: never;
        trains?: never;
        lineNames?: never;
        startTime?: never;
        endTime?: never;
      }
    | {
        variant: "shift";
        startTime?: Date | null;
        endTime?: Date | null;
        stations?: never;
        trains?: never;
        lineNames?: never;
        availableFrom?: never;
        availableTo?: never;
        location?: never;
        reference?: never;
        baseStopReference?: never;
      }
  );

export const VehicleSummary = ({
  variant,
  type,
  stations,
  trains,
  driverPhoneNumber,
  lineNames,
  operatorName,
  startTime,
  endTime,
  availableFrom,
  availableTo,
  location,
  reference,
  baseStopReference,
}: VehicleSummaryProps) => {
  const responsiveThreshold = variant === "reserve-backup" ? 1226 : 2220;
  const typeText = getVehicleText(type);
  const station = useBaseStopStation(baseStopReference);

  const phoneNumberWithDigitsOnly = driverPhoneNumber
    ? driverPhoneNumber.replace("+", "00")
    : null;

  const overflowCropFixPadding = "1px";

  return (
    <SummaryLayout $responsiveThreshold={responsiveThreshold}>
      <Component
        title={operatorName ?? "[Leverandør]"}
        responsiveThreshold={responsiveThreshold}
        child={
          <Box pl={overflowCropFixPadding} pb={overflowCropFixPadding}>
            {phoneNumberWithDigitsOnly ? (
              <CallButton phoneNumber={phoneNumberWithDigitsOnly} size="xs" />
            ) : (
              <Button
                leftIcon={<PhoneOutline24Icon />}
                variant="tertiary"
                disabled={!driverPhoneNumber}
                size="xs"
              >
                Ikke registrert
              </Button>
            )}
          </Box>
        }
        size="lg"
      />

      <Component
        title="Kjøretøy"
        size="sm"
        child={typeText}
        responsiveThreshold={responsiveThreshold}
      />

      {startTime && endTime && (
        <Component
          title="Tidsperiode"
          responsiveThreshold={responsiveThreshold}
          child={
            <TimeInterval
              variant="sm"
              startTime={startTime}
              endTime={endTime}
            />
          }
          size="lg"
        />
      )}

      {availableFrom && availableTo && (
        <Component
          title="Tilgjengelig"
          responsiveThreshold={responsiveThreshold}
          child={
            <TimeInterval
              variant="sm"
              startTime={availableFrom}
              endTime={availableTo}
            />
          }
          size="lg"
        />
      )}

      {stations && (
        <Component
          title="Strekning"
          child={stations}
          size="lg"
          responsiveThreshold={responsiveThreshold}
        />
      )}

      {location && (
        <Component
          title="Lokasjon"
          child={location}
          size="lg"
          responsiveThreshold={responsiveThreshold}
        />
      )}

      {station && (
        <Component
          title="Stasjon"
          child={station.name}
          size="lg"
          responsiveThreshold={responsiveThreshold}
        />
      )}

      {trains && lineNames && (
        <Component
          title="Tog / Linje"
          child={`${trains} / ${lineNames}`}
          responsiveThreshold={responsiveThreshold}
        />
      )}

      {reference && (
        <Component
          title="Referanse"
          child={reference}
          size="sm"
          responsiveThreshold={responsiveThreshold}
        />
      )}
    </SummaryLayout>
  );
};

export const VehicleTitle = ({
  title,
  externalSystem,
  isAvailable = true, // Default to true if not provided, to not show "I bruk" badge
}: {
  title: string;
  externalSystem?: string | null;
  isAvailable?: boolean;
}) => {
  const location = useLocation();

  return (
    <Flex gap="1" alignItems="center" minH="7">
      <Text variant="lg" fontWeight="700">
        {location.state?.vehicleTitle ?? title}
      </Text>
      {externalSystem && (
        <>
          <Text variant="lg" fontWeight="400">
            |
          </Text>

          <Text variant="lg" fontWeight="400">
            {toSentenceCase(externalSystem)}
          </Text>
        </>
      )}

      {!isAvailable && (
        <Badge ml="0.5" colorPalette="blue">
          I bruk
        </Badge>
      )}
    </Flex>
  );
};
